import { isAxiosError } from 'axios';
import {
  addDays,
  addMinutes,
  format,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  parse,
  setHours,
  setMinutes,
} from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Col, FormActions, Icon, Modal, RichTextRenderer, Text, Title, message } from 'ui';
import { isDefaultResponseErrorDataObject } from 'common/services';
import { Translation } from 'features/translations';
import {
  type Activity,
  type PeriodType,
  addPeriodType,
  isBloodPressureType,
  isWithingsWeightType,
} from 'models/activity';
import type { Log } from 'models/service';
import ActivityTasksForm from '../../form/ActivityTasksForm';
import { useCreateTaskLogMutation, useDownloadReportMutation } from '../../queries';
import { useMarkAsCompletedMutation } from '../../queries/mutations/useMarkAsCompletedMutation';

type ActivityTime = {
  endTime: Date;
  startTime: Date;
};

type P = {
  activity: Activity;
  canDownloadReport: boolean;
  canSubmitData: boolean;
  isBookmark?: boolean;
  isProgramPublished: boolean;
  onSuccess: () => Promise<void>;
  reloadingPost: boolean;
  userSubscribed: boolean;
};

const dateFormatReadable = 'MMM d, yyyy';

const ActivityTask = ({
  activity,
  canDownloadReport,
  canSubmitData,
  isBookmark = false,
  isProgramPublished,
  onSuccess,
  reloadingPost,
  userSubscribed,
}: P) => {
  const { t } = useTranslation();
  const [activityData, setActivityData] = useState(activity);
  const isFinished = isAfter(new Date(), activityData.endTime);
  const hasStarted = !isBefore(new Date(), activityData.startTime);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [taskLogsData, setTaskLogsData] = useState<Log[]>([]);
  const [isThankYouModalOpened, setIsThankYouModalOpened] = useState(false);
  const { isPending: isLoadingMarkAsCompleted, mutate: markAsCompleted } = useMarkAsCompletedMutation();
  const { isPending: isLoadingCreateLog, mutate: createTaskLog } = useCreateTaskLogMutation();
  const { isPending: isLoadingDownloadReport, mutate: downloadReport } = useDownloadReportMutation();

  const onSubmit = () => {
    createTaskLog(
      { activityId: activityData.id, body: taskLogsData },
      {
        onError(error) {
          const errorMessage =
            isAxiosError(error) && isDefaultResponseErrorDataObject(error.response?.data)
              ? error.response.data.message
              : 'Failed to record your progress.';
          message.error(errorMessage);
        },
        onSuccess() {
          setIsThankYouModalOpened(true);
        },
      },
    );
  };

  const onDownloadReport = (id: string) => {
    downloadReport(
      { activityId: id },
      {
        onError() {
          message.error('Failed to download report.');
        },
        onSuccess(blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', 'report.csv');
          document.body.append(link);
          link.click();
          link.remove();

          message.success('Report was successfully downloaded.');
        },
      },
    );
  };

  const renderThankYouMessage = () => (
    <div className="activity-core__recorded">
      <Icon className="activity-core__recorded__icon" icon="check-circle-line" />
      <div className="activity-core__recorded__info">
        <Title level={4} marginBottom>
          Thank you for new recording!
        </Title>
      </div>
    </div>
  );

  const renderEndedMessage = () => (
    <Title className="activity-core__info" level={4} marginBottom>
      Activity has ended
    </Title>
  );

  const renderActivityCompleteButton = () => {
    const isDisabled = Boolean(activityData.lastActivitySubmission);

    return (
      <div className="activity-core__action">
        <Button
          disabled={isDisabled}
          loading={isLoadingMarkAsCompleted}
          onClick={() => {
            markAsCompleted(
              { activityId: activityData.id },
              {
                onError(error) {
                  const errorMessage = isDefaultResponseErrorDataObject(error.response?.data)
                    ? error.response.data.message
                    : 'Failed to update activity.';
                  message.error(errorMessage);
                },
              },
            );
          }}
          type="primary"
        >
          {isDisabled ? 'Activity marked as completed' : 'Mark activity as completed'}
        </Button>
      </div>
    );
  };

  const renderWithingsNextPeriodMessage = () => {
    const currentDate = new Date();

    const result = renderPeriod();

    if (result) {
      return result;
    }

    if (activityData.nextPeriodDate && isSameDay(activityData.nextPeriodDate, currentDate)) {
      const withingsDailyTaskMessage = () => {
        if (isBloodPressureType(activityData)) {
          return t('Please take your blood pressure.');
        }

        if (isWithingsWeightType(activityData)) {
          return t('Please take your weight.');
        }

        return t('Please synchronize the withings device with your mobile app today.');
      };

      return (
        <>
          <Title className="activity-core__info" level={4} marginBottom>
            {withingsDailyTaskMessage()}
          </Title>
          {renderActivityCompleteButton()}
        </>
      );
    }

    return (
      <Title className="activity-core__info" level={4} marginBottom>
        {t('activityPeriod', { day: format(activityData.nextPeriodDate ?? new Date(), dateFormatReadable) })}
      </Title>
    );
  };

  const renderState = () => {
    if (!hasStarted) {
      return (
        <Title className="activity-core__info" level={4} marginBottom>
          Activity did not start
        </Title>
      );
    }

    if (isFinished || !activityData.nextPeriodDate) {
      return renderEndedMessage();
    }

    if (!canSubmitData && !canDownloadReport && activityData.isWithings) {
      return renderWithingsNextPeriodMessage();
    }

    return null;
  };

  const getNextPeriodDate = (periodType: keyof PeriodType, nextPeriod: Date) => {
    const nextPeriodDate = addPeriodType(nextPeriod, periodType, 1);

    return isAfter(nextPeriodDate, activityData.endTime) ? (
      renderEndedMessage()
    ) : (
      <Title className="activity-core__info" level={4} marginBottom>
        {t('activityPeriod', {
          day: format(nextPeriodDate, dateFormatReadable),
        })}
      </Title>
    );
  };

  const renderNextDayWithTime = (time: Date, day: Date) => {
    const nextDay = setMinutes(setHours(addDays(day, 1), getHours(time)), getMinutes(time));

    return isAfter(nextDay, activityData.endTime) ? (
      renderEndedMessage()
    ) : (
      <Title className="activity-core__info" level={4} marginBottom>
        {t('activityNotAvailableToday', {
          day: format(nextDay, dateFormatReadable),
          time: format(time, 'p'),
        })}
      </Title>
    );
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity -- TODO: fix
  const renderPeriod = () => {
    const currentDate = new Date();

    if (!activityData.nextPeriodDate) {
      return;
    }

    if (!isSameDay(activityData.nextPeriodDate, currentDate)) {
      return isAfter(activityData.nextPeriodDate, activityData.endTime) ? (
        renderEndedMessage()
      ) : (
        <Title className="activity-core__info" level={4} marginBottom>
          {t('activityPeriod', { day: format(activityData.nextPeriodDate, dateFormatReadable) })}
        </Title>
      );
    }

    const getLastLogTime = () => {
      const lastActivityLogUpdatedAt = activityData.tasks[0]?.logs?.at(-1)?.updated_at;
      if (lastActivityLogUpdatedAt) {
        return new Date(lastActivityLogUpdatedAt);
      }

      let lastLogTime: Date | undefined;

      for (const option of activityData.tasks[0]?.options ?? []) {
        for (const log of option.logs ?? []) {
          const newLogTimeString = log.updated_at;

          if (newLogTimeString && (!lastLogTime || isAfter(new Date(newLogTimeString), lastLogTime))) {
            lastLogTime = new Date(newLogTimeString);
          }
        }
      }

      return lastLogTime;
    };

    const lastLogTime = getLastLogTime();

    if (activityData.times.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      let dateTimes = activityData.times.map((item: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        const time = parse(item.time, 'HH:mm:ssX', new Date());
        return {
          endTime: addMinutes(time, 15),
          startTime: addMinutes(time, -15),
        };
      });

      dateTimes = dateTimes.map((time: ActivityTime, index: number) => {
        const previousEndtime = dateTimes[index - 1]?.endTime;
        if (previousEndtime && isBefore(time.startTime, previousEndtime)) {
          return { ...time, startTime: previousEndtime };
        }

        return time;
      });

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO: fix this
      const availableTime = dateTimes[0]!.startTime;

      if (
        dateTimes.every((time: ActivityTime) => isAfter(new Date(), time.endTime)) ||
        dateTimes.every((time: ActivityTime) => format(new Date(), 'dd') !== format(time.startTime, 'dd'))
      ) {
        return renderNextDayWithTime(availableTime, currentDate);
      }

      if (
        dateTimes.every(
          (time: ActivityTime) => !isWithinInterval(currentDate, { end: time.endTime, start: time.startTime }),
        )
      ) {
        for (const time of dateTimes) {
          if (isAfter(time.startTime, currentDate)) {
            return isAfter(time.startTime, activityData.endTime) ? (
              renderEndedMessage()
            ) : (
              <Title className="activity-core__info" level={4} marginBottom>
                {t('activityAvailableAtAnotherTime', { time: format(time.startTime, 'p') })}
              </Title>
            );
          }
        }
      }

      if (lastLogTime && !activityData.multipleSubmissions) {
        for (const [index, time] of dateTimes.entries()) {
          if (
            isWithinInterval(lastLogTime, { end: time.endTime, start: time.startTime }) &&
            isBefore(currentDate, time.endTime)
          ) {
            return index + 1 === dateTimes.length ? (
              renderNextDayWithTime(availableTime, currentDate)
            ) : // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
            dateTimes[index + 1]?.startTime &&
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Is checked above and present
              isAfter(dateTimes[index + 1]!.startTime, activityData.endTime) ? (
              renderEndedMessage()
            ) : (
              <Title className="activity-core__info" level={4} marginBottom>
                {t('activityAvailableAtAnotherTime', {
                  time: format(dateTimes[index + 1]?.startTime ?? new Date(), 'p'),
                })}
              </Title>
            );
          }
        }
      }
    }

    if (lastLogTime && activityData.periodicityType && activityData.times.length === 0) {
      if (activityData.periodicityType === 'one_time') {
        return renderEndedMessage();
      }

      if (!activityData.multipleSubmissions) {
        return getNextPeriodDate(activityData.periodicityType, activityData.nextPeriodDate);
      }
    }

    return undefined;
  };

  const renderTask = () => {
    const result = renderPeriod();

    if (result) {
      return result;
    }

    return (
      <ActivityTasksForm
        activity={activityData}
        isBookmark={isBookmark}
        isProgramPublished={isProgramPublished}
        loading={isLoadingDownloadReport || isLoadingCreateLog || reloadingPost}
        onSubmit={renderConfirmationModal}
      />
    );
  };

  const renderConfirmationData = () =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    taskLogsData.map((log: any, index: number) => (
      // eslint-disable-next-line react/no-array-index-key -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      <Col className="activity-core__confirmation-item-wrapper" key={`${index}`}>
        {activityData.tasks.map((task, activityDataIndex) => (
          // eslint-disable-next-line react/no-array-index-key -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          <div className="activity-core__confirmation-item" key={`${task.input}-${activityDataIndex}`}>
            {task.input === 'select'
              ? task.options?.find(
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                  (option) => option.id === log[activityDataIndex]?.activity_task_log.activity_task_option_id,
                )?.name
              : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                log[activityDataIndex]?.activity_task_log.value}
          </div>
        ))}
      </Col>
    ));

  const renderConfirmationModal = (taskLogs: Log[]) => {
    setTaskLogsData(taskLogs);
    setIsModalOpened(true);
  };

  useEffect(() => {
    setActivityData(activity);
  }, [activity]);

  return (
    <div className="activity-core">
      {activityData.htmlDescription ? <RichTextRenderer data={activityData.htmlDescription} /> : null}
      {/* TODO Create new Translate component */}
      {activityData.description && activityData.id ? <Translation id={activityData.id} type="activity" /> : null}
      <div className={`activity-core__task-wrapper ${isBookmark ? 'activity-core__disabled' : ''}`}>
        {renderState()}
        {!canDownloadReport &&
        canSubmitData &&
        userSubscribed &&
        !isFinished &&
        !isLoadingDownloadReport &&
        !isLoadingCreateLog
          ? renderTask()
          : null}
        {canDownloadReport && hasStarted ? (
          <div className="activity-core__action">
            <Button
              disabled={isBookmark}
              loading={isLoadingDownloadReport}
              onClick={() => {
                onDownloadReport(activityData.id);
              }}
              type="primary"
            >
              Download report
            </Button>
          </div>
        ) : null}
      </div>
      {isModalOpened ? (
        <Modal
          isOpened={isModalOpened}
          onCancel={() => {
            setIsModalOpened(false);
          }}
          title="Submit your values"
          width="medium"
        >
          <Modal.HugContent>
            <Text className="activity-core__modal-desc">Are you sure you want to submit your response?</Text>
            {renderConfirmationData()}
            <Modal.HugContent.Footer>
              <FormActions
                right={{
                  children: t('Cancel'),
                  onClick() {
                    setIsModalOpened(false);
                  },
                }}
                submit={{
                  children: t('Submit'),
                  onClick() {
                    setIsModalOpened(false);
                    onSubmit();
                  },
                }}
              />
            </Modal.HugContent.Footer>
          </Modal.HugContent>
        </Modal>
      ) : null}
      {isThankYouModalOpened ? (
        <Modal
          isOpened={isThankYouModalOpened}
          onCancel={async () => {
            setIsThankYouModalOpened(false);
            await onSuccess();
          }}
          title="Your progress was recorded."
          width="medium"
        >
          <Modal.HugContent>
            {renderThankYouMessage()}
            <Modal.HugContent.Footer>
              <FormActions
                submit={{
                  children: 'OK',
                  async onClick() {
                    setIsThankYouModalOpened(false);
                    await onSuccess();
                  },
                }}
              />
            </Modal.HugContent.Footer>
          </Modal.HugContent>
        </Modal>
      ) : null}
    </div>
  );
};

export default ActivityTask;
